import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { setAuthToken } from '@/utils/agent';
import type { User } from '@/types/auth';
import { normalizeUser } from '@/utils/user';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials(state, action: PayloadAction<User>) {
      const user = normalizeUser(action.payload);
      state.user = user;
      state.accessToken = user.accessToken ?? null;
      state.refreshToken = user.refreshToken ?? null;
      state.isAuthenticated = Boolean(user.accessToken);
      setAuthToken(user.accessToken ?? null);
    },
    updateTokens(
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>,
    ) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      if (state.user) {
        state.user.accessToken = action.payload.accessToken;
        state.user.refreshToken = action.payload.refreshToken;
      }
      setAuthToken(action.payload.accessToken);
    },
    updateUser(state, action: PayloadAction<User>) {
      if (state.user) {
        state.user = { ...state.user, ...normalizeUser(action.payload) };
      }
    },
    logout(state) {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      setAuthToken(null);
    },
  },
});

export const { setCredentials, updateTokens, updateUser, logout } = authSlice.actions;
export default authSlice.reducer;
